import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ShieldCheck, Loader2, Clock, Fingerprint, KeyRound, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SESSION_OPTIONS = [
  { label: "8 hours", value: 8 },
  { label: "1 day", value: 24 },
  { label: "7 days (default)", value: 168 },
  { label: "30 days", value: 720 },
];

export function SecuritySettings({ open, onClose }) {
  const [hours, setHours] = useState(168);
  const [forceGoogle, setForceGoogle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasRecovery, setHasRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const [genning, setGenning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setRecoveryCode(null);
    api.get("/auth/security")
      .then((r) => { setHours(r.data.session_hours); setForceGoogle(r.data.force_google); setHasRecovery(!!r.data.has_recovery_code); })
      .catch(() => toast.error("Could not load security settings."))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/security", { session_hours: Number(hours), force_google: forceGoogle });
      if (res.data?.recovery_code) {
        setRecoveryCode(res.data.recovery_code);
        setHasRecovery(true);
        toast.success("Saved — copy your recovery code below before closing.");
      } else {
        toast.success("Security settings saved — they apply from your next sign-in.");
        onClose();
      }
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const regen = async () => {
    setGenning(true);
    try {
      const r = await api.post("/auth/security/recovery-code");
      setRecoveryCode(r.data.recovery_code);
      setHasRecovery(true);
    } catch {
      toast.error("Could not generate a recovery code.");
    } finally {
      setGenning(false);
    }
  };

  const copyCode = () => { navigator.clipboard.writeText(recoveryCode).then(() => toast.success("Recovery code copied.")); };

  return createPortal(
    <div className="fixed inset-0 z-[110] bg-black/75 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="security-modal-overlay">
      <div onClick={(e) => e.stopPropagation()} className="glass-panel rounded-2xl p-6 w-full max-w-sm max-h-[88vh] overflow-y-auto" data-testid="security-modal">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display font-semibold text-white text-sm">Security</h3>
          </div>
          <button onClick={onClose} data-testid="security-modal-close" className="text-slate-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-5">Control how long AVA keeps you signed in.</p>

        {loading ? (
          <div className="flex items-center justify-center h-24 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
        ) : (
          <>
            <label className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-slate-400 mb-1.5">
              <Clock className="w-3 h-3" /> Session length
              {forceGoogle && <span className="text-slate-600 normal-case tracking-normal">· session-only while Google-only is on</span>}
            </label>
            <select
              data-testid="security-session-length"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              disabled={forceGoogle}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-1 font-mono-data disabled:opacity-40"
            >
              {SESSION_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-[#0a0f16]">{o.label}</option>)}
            </select>
            <p className="text-[10px] text-slate-600 mb-4">How long a sign-in stays valid before AVA asks you to authenticate again.</p>

            <button
              type="button"
              data-testid="security-force-google-toggle"
              onClick={() => setForceGoogle((v) => !v)}
              className="w-full flex items-center justify-between gap-3 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 mb-2 hover:border-cyan-400/30 transition-colors"
            >
              <span className="flex items-center gap-2 text-left">
                <Fingerprint className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
                <span className="text-[12px] text-slate-200 leading-tight">Force Google sign-in every launch</span>
              </span>
              <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${forceGoogle ? "bg-cyan-400/70" : "bg-white/15"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${forceGoogle ? "left-4.5" : "left-0.5"}`} style={{ left: forceGoogle ? "18px" : "2px" }} />
              </span>
            </button>
            <p className="text-[10px] text-slate-600 mb-2">When on, the session clears when you close the browser and password sign-in is disabled — you must re-authenticate with Google each time you open AVA.</p>
            {forceGoogle && (
              <p className="text-[10px] text-amber-300/80 bg-amber-400/[0.06] border border-amber-400/20 rounded-lg px-2.5 py-2 mb-5 leading-relaxed">
                Heads up: only enable this if Google sign-in reliably works for you. If you ever get locked out, run <span className="font-mono-data text-amber-200">reset_security.py</span> on the backend to restore password sign-in.
              </p>
            )}

            {/* Break-glass recovery code */}
            <div className="mb-5" data-testid="security-recovery-section">
              <label className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-slate-400 mb-1.5">
                <KeyRound className="w-3 h-3" /> Recovery code
              </label>
              {recoveryCode ? (
                <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] p-3">
                  <div className="flex items-center gap-2">
                    <code data-testid="security-recovery-code" className="flex-1 text-sm font-mono-data text-emerald-200 tracking-[0.15em] break-all">{recoveryCode}</code>
                    <button onClick={copyCode} data-testid="security-recovery-copy" className="text-emerald-300 hover:text-emerald-200 shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="text-[10px] text-amber-300/80 mt-2 leading-relaxed">Shown once — save it now. If you're ever locked out, enter it on the login screen to re-enable password sign-in.</p>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono-data text-slate-500">{hasRecovery ? "A recovery code is set." : "No recovery code yet."}</span>
                  <button onClick={regen} disabled={genning} data-testid="security-generate-recovery"
                    className="text-[10px] font-mono-data uppercase tracking-wider text-cyan-300 hover:text-cyan-200 border border-cyan-400/30 rounded-lg px-2 py-1 disabled:opacity-50">
                    {genning ? "…" : hasRecovery ? "Regenerate" : "Generate code"}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={save}
              disabled={saving}
              data-testid="security-save"
              className="w-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-500/25 transition-colors rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </button>
            {recoveryCode && (
              <button onClick={onClose} data-testid="security-done" className="w-full mt-2 text-[11px] font-mono-data text-slate-400 hover:text-white transition-colors py-1">I've saved my code — close</button>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
